/**
 * A dew-point sparkline with the target band drawn behind it.
 *
 * The band is the point. "16.8 °C" means nothing on its own; "16.8, just above
 * the top of the band, and rising" is the whole judgement an operator has to
 * make, and it stays legible at 132×34 pixels once the band is visible.
 *
 * One series, so no legend — the column header names it. The values also appear
 * in adjacent cells and in the title, so identity and magnitude are never
 * carried by colour alone.
 */

interface Props {
  points: { t: string; dew: number | null }[]
  /** The configured band, drawn as a recessive region behind the line. */
  lo?: number | null
  hi?: number | null
  width?: number
  height?: number
  label?: string
}

export default function Sparkline({
  points, lo, hi, width = 132, height = 34, label = 'Dew point',
}: Props) {
  const vals = points.map((p) => p.dew).filter((v): v is number => v != null)
  if (vals.length < 2) {
    return <span className="faint" style={{ fontSize: 12 }}>—</span>
  }

  // The band is part of the domain, so "outside the band" is visibly outside
  // rather than auto-scaled back into the middle of the plot.
  const candidates = [...vals, ...(lo != null ? [lo] : []), ...(hi != null ? [hi] : [])]
  const lowest = Math.min(...candidates)
  const highest = Math.max(...candidates)
  const pad = Math.max(0.3, (highest - lowest) * 0.15)
  const min = lowest - pad
  const span = (highest + pad) - min || 1

  const x = (i: number) => (i / (vals.length - 1)) * (width - 2) + 1
  const y = (v: number) => height - 2 - ((v - min) / span) * (height - 4)

  const d = vals.map((v, i) => `${i ? 'L' : 'M'} ${x(i).toFixed(1)} ${y(v).toFixed(1)}`)
                .join(' ')

  const last = vals[vals.length - 1]
  const outside = (hi != null && last > hi) || (lo != null && last < lo)
  const stroke = outside ? 'var(--text)' : 'var(--text)'

  const bandTop = hi != null ? y(hi) : null
  const bandBottom = lo != null ? y(lo) : null

  return (
    <svg className="spark" width={width} height={height} role="img"
         aria-label={`${label}: latest ${last.toFixed(1)} degrees Celsius`}>
      <title>
        {`${label} — latest ${last.toFixed(1)}°C, `
          + `range ${Math.min(...vals).toFixed(1)}–${Math.max(...vals).toFixed(1)}°C`
          + (lo != null && hi != null
              ? `, band ${lo.toFixed(1)}–${hi.toFixed(1)}°C` : '')}
      </title>

      {bandTop != null && bandBottom != null && (
        <rect x={0} y={bandTop} width={width}
              height={Math.max(1, bandBottom - bandTop)}
              fill="var(--text)" opacity={0.14} rx={2} />
      )}

      {/* 2px line, no per-point markers: at this size a dot per point is noise. */}
      <path d={d} fill="none" stroke={stroke} strokeWidth={2}
            strokeLinecap="round" strokeLinejoin="round" />

      {/* Only the latest point is marked — that is the one being judged. */}
      <circle cx={x(vals.length - 1)} cy={y(last)} r={3}
              fill={stroke} stroke="var(--surface)" strokeWidth={2} />
    </svg>
  )
}
